import React, { useState, useEffect } from 'react';
import { getStatusFromServer, getStatusListener } from '../services/firebaseService';
import useAppUpdater from '../services/useAppUpdater';
import StatusIndicator from '../components/StatusIndicator';
import AdminMessage from '../components/AdminMessage';
import LastHeartbeat from '../components/LastHeartbeat';
import UptimeStats from '../components/UptimeStats';
import '../styles/StatusPage.css';
import '../styles/ActivityStatus.css';

const MONITORING_STARTED_AT = new Date(2026, 7, 25, 0, 0, 0);
const FRESHNESS_WINDOW_MS = 45 * 1000;
const SERVER_REFRESH_INTERVAL_MS = 15 * 1000;

const formatMonitoringDuration = (now) => {
  const end = new Date(now);
  if (end <= MONITORING_STARTED_AT) return '0h 0m';

  const cursor = new Date(MONITORING_STARTED_AT);
  let years = end.getFullYear() - cursor.getFullYear();
  cursor.setFullYear(cursor.getFullYear() + years);
  if (cursor > end) {
    years -= 1;
    cursor.setFullYear(cursor.getFullYear() - 1);
  }

  let months = (end.getFullYear() - cursor.getFullYear()) * 12 + end.getMonth() - cursor.getMonth();
  cursor.setMonth(cursor.getMonth() + months);
  if (cursor > end) {
    months -= 1;
    cursor.setMonth(cursor.getMonth() - 1);
  }

  const remainingMinutes = Math.max(0, Math.floor((end - cursor) / 60000));
  const weeks = Math.floor(remainingMinutes / (7 * 24 * 60));
  const days = Math.floor((remainingMinutes % (7 * 24 * 60)) / (24 * 60));
  const hours = Math.floor((remainingMinutes % (24 * 60)) / 60);
  const minutes = remainingMinutes % 60;
  const parts = [];

  if (years > 0) parts.push(`${years}y`);
  if (years > 0 || months > 0) parts.push(`${months}m`);
  if (weeks > 0) parts.push(`${weeks}w`);
  if (days > 0) parts.push(`${days}d`);
  parts.push(`${hours}h`, `${minutes}m`);
  return parts.join(' ');
};

export default function StatusPage() {
  useAppUpdater();

  const [status, setStatus] = useState({
    lastHeartbeat: null,
    adminMessage: '',
    activeStreamCount: null,
    activityUpdatedAt: null,
    uptimeTrackingStartedAt: null,
    uptimeDowntime24Hours: 0,
    uptimeDowntime7Days: 0,
    uptimeDowntime30Days: 0,
    uptimeTotalDowntimeSeconds: 0,
    hasLoaded: false,
    lastServerConfirmationAt: null
  });
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const applyServerStatus = (data) => {
      setStatus({
        lastHeartbeat: data.lastHeartbeat,
        adminMessage: data.adminMessage || '',
        activeStreamCount: data.activeStreamCount,
        activityUpdatedAt: data.activityUpdatedAt,
        uptimeTrackingStartedAt: data.uptimeTrackingStartedAt,
        uptimeDowntime24Hours: data.uptimeDowntime24Hours,
        uptimeDowntime7Days: data.uptimeDowntime7Days,
        uptimeDowntime30Days: data.uptimeDowntime30Days,
        uptimeTotalDowntimeSeconds: data.uptimeTotalDowntimeSeconds,
        hasLoaded: true,
        lastServerConfirmationAt: Date.now()
      });
    };

    const refreshFromServer = async () => {
      try {
        applyServerStatus(await getStatusFromServer());
      } catch (error) {
        // Keep the last trusted screen only briefly. The render below hides it
        // after FRESHNESS_WINDOW_MS instead of fabricating an outage.
        console.warn('Unable to confirm fresh Plex status from Firestore:', error);
      }
    };

    const unsubscribe = getStatusListener((data, metadata) => {
      if (!metadata?.fromCache) applyServerStatus(data);
    });
    refreshFromServer();

    const refreshTimer = setInterval(refreshFromServer, SERVER_REFRESH_INTERVAL_MS);
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refreshFromServer();
    };
    window.addEventListener('online', refreshFromServer);
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      unsubscribe();
      clearInterval(refreshTimer);
      window.removeEventListener('online', refreshFromServer);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Never render cached status as if it were live. This also removes the
  // provisional red state during initial loading.
  if (!status.hasLoaded) {
    return <div className="status-page" aria-busy="true" />;
  }

  const statusIsFresh = now - status.lastServerConfirmationAt < FRESHNESS_WINDOW_MS;
  const getStatusLevel = () => {
    if (!statusIsFresh) return 'unavailable';
    if (!status.lastHeartbeat) return 'offline';
    const age = now - new Date(status.lastHeartbeat).getTime();
    if (age < 90 * 1000) return 'online';
    if (age < 120 * 1000) return 'warning';
    return 'offline';
  };

  const statusLevel = getStatusLevel();
  const indicatorLevel = statusLevel === 'unavailable' ? 'loading' : statusLevel;
  const getLastCheckTime = () => new Date(status.lastHeartbeat)
    .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  const getViewingText = () => {
    const activityIsFresh = status.activityUpdatedAt
      && now - new Date(status.activityUpdatedAt).getTime() < 2 * 60 * 1000;
    if (statusLevel !== 'online' || !activityIsFresh || Number(status.activeStreamCount) < 1) return null;
    if (Number(status.activeStreamCount) === 1) return '1 person is watching';
    return `${status.activeStreamCount} people are watching`;
  };

  const viewingText = getViewingText();
  const monitoringDuration = formatMonitoringDuration(now);
  const statusText = {
    online: 'Online',
    warning: 'Potential Issues',
    offline: 'Plex is Offline',
    unavailable: 'Status temporarily unavailable'
  }[statusLevel];
  const statusMessage = {
    online: 'Plex is currently online',
    warning: 'Service may be experiencing issues',
    offline: 'Service is currently unavailable',
    unavailable: 'This device has not received a fresh status update.'
  }[statusLevel];

  return (
    <div className="status-page">
      <a href="/admin" className="gear-icon" title="Admin Panel" aria-label="Admin Panel">⚙️</a>
      <div className="status-container">
        <h1><span>Plex</span><span>Server</span></h1>
        <div className="status-indicator-container">
          <StatusIndicator level={indicatorLevel} />
          <h2 className={`status-text status-${indicatorLevel}`}>{statusText}</h2>
        </div>
        <p className="status-message">{statusMessage}</p>
        {statusIsFresh && <>
          {viewingText && <p className="viewing-count">{viewingText}</p>}
          <LastHeartbeat timestamp={status.lastHeartbeat} />
          {status.adminMessage && <AdminMessage message={status.adminMessage} />}
          <UptimeStats status={status} now={now} />
        </>}
        <div className="footer">
          <p className="update-frequency">Status checks every 30 seconds</p>
          {statusIsFresh && status.lastHeartbeat && <p className="last-check-time heartbeat-ready">Last confirmed online: {getLastCheckTime()}</p>}
          <p className="monitoring-since">Monitoring since 25 August 2026</p>
          <p className="monitoring-since monitoring-duration">{monitoringDuration}</p>
        </div>
      </div>
    </div>
  );
}
